import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import DOMPurify from 'dompurify'
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Collapse,
  Tab,
  Tabs
} from '@mui/material'
import Header from '@/src/components/announcement-header'
import { Footer } from '@/src/components/footer'
import CommonApi from '@/pages/api'
import { CorporateGovernance } from '@/pages/api/endpoints/CorporateGovernance'
import { useStore } from '@/src/types/store'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import ArticleIcon from '@mui/icons-material/Article'
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import PieChartIcon from '@mui/icons-material/PieChart'
import ShieldIcon from '@mui/icons-material/Shield'

interface GovernanceItem {
  id: number
  title: string
  description: string | null
  type: 'pdf' | 'link' | 'richtext'
  content: string | null
  file_url: string | null
  link_url: string | null
  status: string
  publish_date: string | null
  order: number
  label: string | null
}

interface Shareholder {
  position: number
  name: string
  shares: number
  percentage: number
}

interface ShareholderData {
  shareholders: Shareholder[]
  total_shares: number
  total_percentage: number
  as_at_date: string
  total_issued_capital: number
}

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-AU').format(num)
}

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Deep-linking: the Top 20 Shareholders tab is reachable at
// /corporate?tab=top-20-shareholders. Corporate Governance is the default and
// carries NO tab param, so a bare /corporate lands on it (and selecting it
// strips the param back off).
const TOP_20_SLUG = 'top-20-shareholders'

// Coerce router.query[key] (string | string[] | undefined) to one string.
const firstQueryValue = (value: string | string[] | undefined): string | null => {
  if (Array.isArray(value)) return value[0] ?? null
  return value && value.length > 0 ? value : null
}

const CorporatePage = () => {
  const { themeLoading } = useStore()
  const router = useRouter()
  const isPreview = router.query.preview === 'true'
  const [items, setItems] = useState<GovernanceItem[]>([])
  const [shareholderData, setShareholderData] = useState<ShareholderData | null>(null)
  const [loading, setLoading] = useState(true)
  const [shLoading, setShLoading] = useState(true)
  const [showShareholders, setShowShareholders] = useState(true)
  const [expandedItem, setExpandedItem] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState(0)
  const [sectionEnabled, setSectionEnabled] = useState<boolean | null>(null)

  const fetchGovernance = useCallback(async () => {
    setLoading(true)
    try {
      // Preview mode omits the status filter; the backend enforces auth and
      // returns only published items for unauthenticated callers.
      const response = await CommonApi({
        path: CorporateGovernance.list(isPreview ? undefined : 'published'),
        method: 'get'
      })
      if (response?.data) {
        setItems(response.data.items || [])
      }
    } catch (err) {
      console.error('Error fetching governance data:', err)
    } finally {
      setLoading(false)
    }
  }, [isPreview])

  const fetchShareholders = useCallback(async () => {
    setShLoading(true)
    try {
      const response = await CommonApi({
        path: CorporateGovernance.topShareholders(20),
        method: 'get'
      })
      if (response?.data) {
        setShareholderData(response.data)
      }
    } catch (err) {
      console.error('Error fetching shareholders:', err)
    } finally {
      setShLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchGovernance()
    fetchShareholders()

    // Check if the Corporate Governance section is enabled on the homepage.
    // Skip this check in preview mode so admins can preview disabled sections.
    if (!isPreview) {
      const checkSectionVisibility = async () => {
        try {
          const resp = await CommonApi({
            path: 'show-homepage-sections',
            method: 'get'
          })
          if (resp?.status && Array.isArray(resp.data)) {
            const cgSection = resp.data.find((s: any) => s.title === 'Corporate Governance')
            if (cgSection && !cgSection.show_section) {
              router.push('/')
              return
            }
          }
        } catch {
          // Fail open — allow page to render
        }
        setSectionEnabled(true)
      }
      checkSectionVisibility()
    } else {
      setSectionEnabled(true)
    }

    const fetchSettings = async () => {
      try {
        const resp = await CommonApi({
          path: CorporateGovernance.settings,
          method: 'get'
        })
        if (resp?.data) {
          const showTop = resp.data.show_top_shareholders ?? true
          setShowShareholders(showTop)
          // When Top 20 is disabled, the URL→tab sync effect clamps the active
          // tab back to Corporate Governance (and strips any stale deep-link
          // param), so no manual reset is needed here.
        }
      } catch (err) {
        console.error('Error fetching corporate governance settings:', err)
      }
    }
    fetchSettings()
  }, [fetchGovernance, fetchShareholders, isPreview, router])

  // URL → active tab. Single source of truth is the `tab` query param, so this
  // covers the initial deep link, browser back/forward, and any programmatic
  // URL change. `top-20-shareholders` selects the Top 20 tab; anything else
  // (absent / unknown value) falls back to Corporate Governance. If the Top 20
  // tab is disabled by settings, a stale deep link is clamped to governance
  // and the param is stripped so the URL matches what's shown.
  useEffect(() => {
    if (!router.isReady) return
    const wantsTop20 = firstQueryValue(router.query.tab) === TOP_20_SLUG
    if (wantsTop20 && showShareholders) {
      setActiveTab(1)
      return
    }
    setActiveTab(0)
    if (wantsTop20 && !showShareholders) {
      const query = { ...router.query }
      delete query.tab
      router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
        scroll: false
      })
    }
  }, [router.isReady, router.query.tab, showShareholders, router])

  // Tab click → push the URL (shallow, no reload); the effect above reflects it
  // back into state. Preserves any other query params (e.g. preview=true).
  const handleTabChange = useCallback(
    (nextTab: number) => {
      const query = { ...router.query }
      if (nextTab === 1) query.tab = TOP_20_SLUG
      else delete query.tab
      router.push({ pathname: router.pathname, query }, undefined, {
        shallow: true,
        scroll: false
      })
    },
    [router]
  )

  const groupedItems = useMemo(() => {
    const groups = new Map<string, GovernanceItem[]>()
    for (const item of items) {
      const key = item.label?.trim() || ''
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(item)
    }
    return Array.from(groups.entries())
  }, [items])

  if (themeLoading || sectionEnabled === null) {
    return (
      <Box className="h-screen flex items-center justify-center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Header />
      <div
        className="lg:max-w-screen overflow-y-auto bg-[#f5f5f8]"
        style={{
          height: 'calc(100vh - 100px)',
          marginTop: '6rem',
          transition: 'margin-top 1s ease-in-out'
        }}
      >
        {/* ──── Tab Bar ──── */}
        <Box className="px-[1rem] md:px-[4.25rem] lg:px-[10.25rem] xl:max-w-[1650px] xl:mx-auto pt-6">
          <Tabs
            value={activeTab}
            onChange={(_, v) => handleTabChange(v)}
            className="!min-h-0"
            TabIndicatorProps={{ className: '!bg-primary-main !h-[3px] !rounded-full' }}
          >
            <Tab
              label="Corporate Governance"
              className={`!normal-case !text-[15px] !font-semibold !min-h-0 !py-2 ${
                activeTab === 0 ? '!text-primary-main' : ''
              }`}
            />
            {showShareholders && (
              <Tab
                label="Top 20 Shareholders"
                className={`!normal-case !text-[15px] !font-semibold !min-h-0 !py-2 ${
                  activeTab === 1 ? '!text-primary-main' : ''
                }`}
              />
            )}
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <div className="min-h-[calc(100vh-164px)] px-[1rem] md:px-[4.25rem] lg:px-[10.25rem] py-[1rem] md:py-[3.375rem] xl:max-w-[1650px] xl:mx-auto">
            <div className="w-full pb-8">
              {loading ? (
                <Box className="flex justify-center py-16">
                  <CircularProgress />
                </Box>
              ) : items.length === 0 ? (
                <Box className="w-full h-32 flex items-center justify-center">
                  <Typography variant="body1" color="text.secondary">
                    No corporate governance documents available.
                  </Typography>
                </Box>
              ) : (
                <div className="flex flex-col gap-10">
                  {groupedItems.map(([label, groupItems]) => (
                    <Box key={`group:${label}`} className="flex flex-col gap-4">
                      <Typography variant="h5" className="border-b border-gray-200 pb-2">
                        {label || 'Other'}
                      </Typography>
                      <div className="flex flex-col gap-4">
                        {groupItems.map(item => (
                          <GovernanceRow
                            key={item.id}
                            item={item}
                            expanded={expandedItem === item.id}
                            onToggle={() =>
                              setExpandedItem(expandedItem === item.id ? null : item.id)
                            }
                          />
                        ))}
                      </div>
                    </Box>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ──── Top 20 Shareholders Tab ──── */}
        {activeTab === 1 && (
          <div className="px-[1rem] md:px-[4.25rem] lg:px-[10.25rem] py-[1rem] md:py-[3.375rem] min-h-[calc(100vh-164px)] xl:max-w-[1650px] xl:mx-auto">
            {shLoading ? (
              <Box className="flex justify-center py-16">
                <CircularProgress />
              </Box>
            ) : shareholderData ? (
              <Box className="flex flex-col gap-6">
                {/* KPI Row */}
                <Box className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="rounded-[20px] bg-white p-4 flex items-center gap-3">
                    <Box className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                      <PeopleAltIcon className="text-blue-500" fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Holders
                      </Typography>
                      <Typography variant="subtitle1">
                        {shareholderData.shareholders.length}
                      </Typography>
                    </Box>
                  </div>
                  <div className="rounded-[20px] bg-white p-4 flex items-center gap-3">
                    <Box className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                      <PieChartIcon className="text-green-600" fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Top 20 Coverage
                      </Typography>
                      <Typography variant="subtitle1">
                        {shareholderData.total_percentage}%
                      </Typography>
                    </Box>
                  </div>
                  <div className="rounded-[20px] bg-white p-4 flex items-center gap-3">
                    <Box className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <ShieldIcon className="text-purple-500" fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        As at
                      </Typography>
                      <Typography variant="subtitle1">
                        {formatDate(shareholderData.as_at_date)}
                      </Typography>
                    </Box>
                  </div>
                </Box>

                {/* Table */}
                <TableContainer
                  component={Paper}
                  className="!rounded-2xl !shadow-sm border border-gray-100"
                >
                  <Table>
                    <TableHead>
                      <TableRow className="bg-gray-50">
                        <TableCell className="!w-16">
                          <Typography
                            variant="tableHeader"
                            color="text.secondary"
                            className="uppercase tracking-wider"
                          >
                            #
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="tableHeader"
                            color="text.secondary"
                            className="uppercase tracking-wider"
                          >
                            Name
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="tableHeader"
                            color="text.secondary"
                            className="uppercase tracking-wider"
                          >
                            Shares
                          </Typography>
                        </TableCell>
                        <TableCell align="right" className="!w-36">
                          <Typography
                            variant="tableHeader"
                            color="text.secondary"
                            className="uppercase tracking-wider"
                          >
                            % of Capital
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shareholderData.shareholders.map(holder => (
                        <TableRow key={holder.position} className="hover:bg-gray-50/50">
                          <TableCell>
                            <Chip
                              label={holder.position}
                              size="small"
                              className={`!min-w-[2rem] ${
                                holder.position <= 3
                                  ? '!bg-primary-main/10 !text-primary-main'
                                  : '!bg-gray-100 !text-secondary-main'
                              }`}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{holder.name}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{formatNumber(holder.shares)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box className="flex items-center justify-end gap-2">
                              <Box
                                className="h-2 rounded-full bg-primary-main/20 overflow-hidden hidden sm:block"
                                style={{ width: '50px' }}
                              >
                                <Box
                                  className="h-full bg-primary-main rounded-full"
                                  style={{
                                    width: `${Math.min((holder.percentage / 20) * 100, 100)}%`
                                  }}
                                />
                              </Box>
                              <Typography variant="body2">{holder.percentage}%</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-50 border-t-2">
                        <TableCell />
                        <TableCell>
                          <Typography variant="subtitle2">Total</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2">
                            {formatNumber(shareholderData.total_shares)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2">
                            {shareholderData.total_percentage}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="caption" color="text.secondary" className="text-center">
                  Data sourced from the company share registry as at{' '}
                  {formatDate(shareholderData.as_at_date)}. Beneficial ownership behind nominee
                  accounts may not be fully reflected.
                </Typography>
              </Box>
            ) : null}
          </div>
        )}
        <Footer />
      </div>
    </>
  )
}

/* ──── Full-width Governance Row ──── */
interface RowProps {
  item: GovernanceItem
  expanded: boolean
  onToggle: () => void
}

const typeConfig = {
  pdf: { icon: <PictureAsPdfIcon className="text-red-500" />, bg: 'bg-red-50', label: 'PDF' },
  link: { icon: <InsertLinkIcon className="text-blue-500" />, bg: 'bg-blue-50', label: 'Link' },
  richtext: { icon: <ArticleIcon className="text-green-600" />, bg: 'bg-green-50', label: 'Text' }
}

const GovernanceRow = ({ item, expanded, onToggle }: RowProps) => {
  const cfg = typeConfig[item.type] ?? typeConfig.pdf

  return (
    <div className="rounded-[20px] bg-white p-4 grid gap-3 overflow-hidden">
      {/* Title row with icon + action on far right */}
      <div className="flex items-center gap-3">
        <Box className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
          {cfg.icon}
        </Box>
        <Box className="flex-1 min-w-0">
          {(item.type === 'pdf' && item.file_url) || (item.type === 'link' && item.link_url) ? (
            <Typography
              variant="subtitle1"
              className="break-words cursor-pointer hover:underline hover:text-primary-main transition-colors"
              onClick={() =>
                window.open(
                  item.type === 'pdf' ? item.file_url! : item.link_url!,
                  '_blank',
                  'noopener,noreferrer'
                )
              }
            >
              {item.title}
            </Typography>
          ) : item.type === 'richtext' && item.content ? (
            <Typography
              variant="subtitle1"
              className="break-words cursor-pointer hover:underline hover:text-primary-main transition-colors"
              onClick={onToggle}
            >
              {item.title}
            </Typography>
          ) : (
            <Typography variant="subtitle1" className="break-words">
              {item.title}
            </Typography>
          )}
        </Box>

        {/* Far-right action */}
        {((item.type === 'pdf' && item.file_url) || (item.type === 'link' && item.link_url)) && (
          <Box
            className="shrink-0 w-9 h-9 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors"
            title="Download"
            onClick={() =>
              window.open(
                item.type === 'pdf' ? item.file_url! : item.link_url!,
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            <DownloadForOfflineIcon className="text-icon-lightGrey" fontSize="small" />
          </Box>
        )}
        {item.type === 'richtext' && item.content && (
          <Button
            size="small"
            variant="outlined"
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={onToggle}
            className="!normal-case !rounded-full shrink-0"
          >
            {expanded ? 'Collapse' : 'Read'}
          </Button>
        )}
      </div>

      {/* Description */}
      {item.description && (
        <Typography variant="body2" color="text.secondary" className="break-words">
          {item.description}
        </Typography>
      )}

      {/* Expanded Rich Text Content */}
      {item.type === 'richtext' && item.content && expanded && (
        <Collapse in>
          <Box className="border-t border-gray-100 pt-3 mt-1">
            <Box
              className="prose prose-sm max-w-none"
              sx={{ color: 'text.secondary' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }}
            />
          </Box>
        </Collapse>
      )}
    </div>
  )
}

export default CorporatePage
